/*
 * Copyright (c) 2020.  Rikesh Lal Shrestha
 * All Rights Reserved
 */

import React, { createContext, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import {
  API_URL,
  ERROR_MESSAGE,
  CLIENT_JWT_TOKEN,
  CLIENT_USER_FULL_NAME,
  CLIENT_ENFORCE_UPDATE,
  CLIENT_USER_TYPE,
  CLIENT_CLIENT_TYPE,
  CLIENT_USER_ID,
  CLIENT_USER_EMAIL,
  CLIENT_SELECTED_ASSOCIATION_ID,
  CLIENT_SELECTED_ASSOCIATION_TYPE,
  CLIENT_ACCESS_CODE,
  CLIENT_COMPANY_DATA,
  CLIENT_IS_PRIMARY_ACCOUNT,
  CLIENT_PROFILE,
  CLIENT_ENFORCE_PASSWORD,
  CLIENT_TEMP_EMAIL,
  CLIENT_IS_SPOUSE,
  CLIENT_ENCRYPTION_KEY,
  CLIENT_USER_SPOUSE_ID,
  CLIENT_USER_PATHS,
  CLIENT_COUNTRY_CODE,
  CLIENT_OTP_VERIFICATION_CODE,
  CLIENT_SELECTED_LANGUAGE,
  CLIENT_TAX_REPRESENTATIVE_ID,
  CLIENT_TAX_REPRESENTATIVE_EMAIL,
  CLIENT_TAX_REPRESENTATIVE_NAME,
  CLIENT_SWITCHED_ID,
} from '../../../constants/appConfig';
import { clearLocalStorage, getLocalStorage, setLocalStorage } from '../../../utils/storageUtil';
import { isClientAuthenticated } from '../../../utils/jwtUtil';
import history from '../../../utils/history';
import {
  clientAppAskForPermissionToReceiveNotifications,
  clientAppUnregisterFirebase,
} from '../../../pushnotification';
import { clientLoginLocalStorage, clientLogoutLocalStorage } from '../../../utils/authUtil';
import { getCurrentTimeZone, isBrowserSafari, isEmpty } from '../../../utils/commonUtil';
import { isFirebaseSupported } from '../../../FirebaseCLientConfig';

const ClientAuthContext = createContext({});

const ClientAuthProvider = (props) => {
  const [user, setUser] = useState(getLocalStorage('user') || {});
  const [loading, setLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [message, setMessage] = useState({});
  const [fields, setFields] = useState({
    userId: '',
    password: '',
    accessCode: '',
  });
  const [sideBarMenu, setSideBarMenu] = useState('users');
  const [primaryColor, setPrimaryColor] = useState('#466FEE');
  const [secondaryColor, setSecondaryColor] = useState('#3CD4A0');
  const [chatPopUpDisplay, setChatPopUpDisplay] = useState(false);

  // Client App
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [clientUserFullName, setUserClientFullName] = useState(
    getLocalStorage(CLIENT_USER_FULL_NAME) || {}
  );
  const [clientEmail, setClientEmail] = useState(getLocalStorage(CLIENT_USER_EMAIL) || undefined);
  const [clientUserId, setClientUserId] = useState(getLocalStorage(CLIENT_USER_ID) || {});
  const [clientUserType, setClientUserType] = useState(getLocalStorage(CLIENT_USER_TYPE) || {});
  const [clientAuthenticated, setClientAuthenticated] = useState(isClientAuthenticated() || false);
  const [clientAccessCode, setClientAccessCode] = useState(getLocalStorage(CLIENT_ACCESS_CODE));
  const [clientType, setClientType] = useState(getLocalStorage(CLIENT_CLIENT_TYPE) || {});
  const [enforceUpdateStatus, setEnforceUpdateStatus] = useState(
    getLocalStorage(CLIENT_ENFORCE_UPDATE) || false
  );
  const [clientCompanyData, setClientCompanyData] = useState(getLocalStorage(CLIENT_COMPANY_DATA));
  const [clientProfile, setClientProfile] = useState(getLocalStorage(CLIENT_PROFILE));

  const [isPrimaryAccount, setIsPrimaryAccount] = useState(
    getLocalStorage(CLIENT_IS_PRIMARY_ACCOUNT) || false
  );

  const [isSpouseAccount, setIsSpouseAccount] = useState(
    getLocalStorage(CLIENT_IS_SPOUSE) || false
  );
  const [clientSpouseId, setClientSpouseId] = useState(
    getLocalStorage(CLIENT_USER_SPOUSE_ID) || {}
  );

  const isLinkedSpouseAccount = Boolean(isSpouseAccount) && !isPrimaryAccount;
  // Client App Selected Associations
  const [selectedAssociationType, setSelectedAssociationType] = useState(
    getLocalStorage(CLIENT_SELECTED_ASSOCIATION_TYPE) || undefined
  );
  const [selectedAssociationId, setSelectedAssociationId] = useState(
    getLocalStorage(CLIENT_SELECTED_ASSOCIATION_ID) || undefined
  );
  const [clientEncryptedKey, setClientEncryptedKey] = useState(
    getLocalStorage(CLIENT_ENCRYPTION_KEY || {})
  );

  const [profileSwitchPaths, setProfileSwitchPaths] = useState(
    getLocalStorage(CLIENT_USER_PATHS) ? getLocalStorage(CLIENT_USER_PATHS) : []
  );

  const [clientCountryCode, setClientCountryCode] = useState(
    getLocalStorage(CLIENT_COUNTRY_CODE) || ''
  );

  const [verificationId, setVerificationId] = useState(
    getLocalStorage(CLIENT_OTP_VERIFICATION_CODE) || ''
  );
  const [clientSelectedLanguage, setClientSelectedLanguage] = useState(
    getLocalStorage(CLIENT_SELECTED_LANGUAGE) || 'en'
  );

  const [taxRepresentativeID, setTaxRepresentativeID] = useState(
    getLocalStorage(CLIENT_TAX_REPRESENTATIVE_ID) || {}
  );
  const [taxRepresentativeEmail, setTaxRepresentativeEmail] = useState(
    getLocalStorage(CLIENT_TAX_REPRESENTATIVE_EMAIL) || {}
  );
  const [taxRepresentativeName, setTaxRepresentativeName] = useState(
    getLocalStorage(CLIENT_TAX_REPRESENTATIVE_NAME) || {}
  );

  const [switchedClientId, setSwitchedClientId] = useState(
    getLocalStorage(CLIENT_SWITCHED_ID) || {}
  );

  const [clientFormError, setClientFormError] = useState({ message: '' });

  const state = {
    user,
    loading,
    clientAuthenticated,
    clientUserType,
    enforceUpdateStatus,
    clientUserId,
    clientUserFullName,
    clientAccessCode,
    clientType,
    selectedAssociationType,
    selectedAssociationId,
    setSelectedAssociationType,
    clientCompanyData,
    companyLoading,
    clientEmail,
    documentUploaded,
    setSelectedAssociationId,
    isPrimaryAccount,
    chatPopUpDisplay,
    setChatPopUpDisplay,
    clientProfile,
    isSpouseAccount,
    isLinkedSpouseAccount,
    clientEncryptedKey,
    clientSpouseId,
    profileSwitchPaths,
    clientCountryCode,
    verificationId,
    clientSelectedLanguage,
    taxRepresentativeID,
    taxRepresentativeEmail,
    taxRepresentativeName,
    switchedClientId,
    clientFormError,
  };

  const cleanClientAuthContextClean = () => {
    setLoading(false);
    setCompanyLoading(false);
    setMessage(undefined);
    setFields({ userId: '', password: '', accessCode: '' });
  };

  const clientRegister = (firebaseReceivedToken) => {
    return axios
      .create({
        baseURL: `${API_URL}`,
        headers: {
          'X-XSRF-TOKEN': getLocalStorage(CLIENT_JWT_TOKEN),
          'X-TIME-ZONE': getCurrentTimeZone(),
        },
      })
      .put('/atom-client/v1/notifications/register/token', {
        token: firebaseReceivedToken,
      })
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
      })
      .catch((error) => {
        console.info('Firebase::', error.response.data.message);
      });
  };

  const clientLoginSuccess = (responseData, accessCode, oauth = false) => {
    clientLoginLocalStorage(responseData, accessCode);
    setClientProfile(responseData);
    setIsPrimaryAccount(responseData?.primaryAccount);
    setClientEmail(responseData.email);
    setUserClientFullName(responseData.fullName);
    setClientUserId(responseData.id);
    setClientType(responseData.clientType);
    setSelectedAssociationId(responseData.id);

    setSelectedAssociationType(responseData.clientType);
    setClientUserType('CLIENT');
    setMessage(undefined);
    setClientAuthenticated(true);
    setEnforceUpdateStatus(responseData.enforceUpdate);
    setSideBarMenu('client');
    setLoading(false);
    setClientAccessCode(responseData?.accessCode);
    setIsSpouseAccount(responseData?.isSpouse);
    setClientEncryptedKey(responseData?.encryptionKey);
    setClientSpouseId(responseData?.spouseId);
    setClientCountryCode(responseData?.countryCode);
    setTaxRepresentativeID(responseData?.userInfo?.data);
    setTaxRepresentativeEmail(responseData?.userInfo?.code);
    setTaxRepresentativeName(responseData?.userInfo?.title);
    setLocalStorage(CLIENT_TAX_REPRESENTATIVE_ID, responseData?.userInfo?.data);
    setLocalStorage(CLIENT_TAX_REPRESENTATIVE_EMAIL, responseData?.userInfo?.code);
    setLocalStorage(CLIENT_TAX_REPRESENTATIVE_NAME, responseData?.userInfo?.title);
    setClientSelectedLanguage('en');
    setVerificationId('');
    setSwitchedClientId(
      responseData?.isSpouse && !responseData?.primaryAccount
        ? responseData?.spouseId
        : responseData.id
    );

    setTimeout(() => {
      if (isEmpty(getLocalStorage(CLIENT_USER_PATHS))) {
        setProfileSwitchPaths([]);
        setProfileSwitchPaths([
          {
            title: responseData.fullName,
            code: responseData?.primaryAccount ? responseData.id : responseData?.spouseId,
            type: responseData.clientType,
            isPrimary: responseData?.primaryAccount,
            isSpouseAccount: responseData?.isSpouse,
            isMainProfile: responseData.id || responseData?.spouseId ? true : false,
          },
        ]);
        setLocalStorage(CLIENT_USER_PATHS, [
          {
            title: responseData.fullName,
            code: responseData?.primaryAccount ? responseData.id : responseData?.spouseId,
            type: responseData.clientType,
            isPrimary: responseData?.primaryAccount,
            isSpouseAccount: responseData?.isSpouse,
            isMainProfile: responseData.id || responseData?.spouseId ? true : false,
          },
        ]);
      }
      if (responseData?.enforcePasswordChange) {
        setLocalStorage(CLIENT_ENFORCE_PASSWORD, true);
        history.push('/client/enforcepassword');
      } else {
        if (getLocalStorage(CLIENT_ENFORCE_PASSWORD)) {
          clearLocalStorage(CLIENT_ENFORCE_PASSWORD);
          clearLocalStorage(CLIENT_TEMP_EMAIL);
        }

        if (!oauth && history?.location?.search) {
          history.push(history?.location?.search?.split('=')[1]);
        } else {
          history.push('/client/dashboard');
        }
      }

      if (!isBrowserSafari() && isFirebaseSupported()) {
        clientAppAskForPermissionToReceiveNotifications().then((token) => {
          if (token) {
            clientRegister(token);
          }
        });
      }
    }, 1000);
  };

  const clientLogin = ({ userName, password, accessCode }) => {
    setLoading(true);
    return axios
      .post(
        API_URL + '/atom-client/v1/auths',
        { userName, password, accessCode },
        {
          headers: {
            'X-TIME-ZONE': getCurrentTimeZone(),
          },
        }
      )
      .then((response) => {
        if (response.status === 200) {
          clientLoginSuccess(response.data.data, accessCode);
          if (
            !response?.data?.data?.primaryAccount &&
            response?.data?.data?.enforcePasswordChange
          ) {
            setLocalStorage(CLIENT_TEMP_EMAIL, userName);
          }
          return response.data;
        }
      })
      .catch((error) => {
        setLoading(false);
        setMessage(error?.response?.data ? error?.response?.data : ERROR_MESSAGE);
      });
  };

  const clientCompanyDataClean = () => {
    setClientCompanyData({});
    setLocalStorage(CLIENT_COMPANY_DATA, {});
  };

  const clientCompanyDataFetch = (accessCode) => {
    setCompanyLoading(true);
    return axios
      .get(API_URL + `/atom-client/v1/company/public/${accessCode}/company-detail`, {
        headers: {
          'X-TIME-ZONE': getCurrentTimeZone(),
        },
      })
      .then((response) => {
        setCompanyLoading(false);
        if (response.status === 200) {
          setClientCompanyData(response.data.data);
          setLocalStorage(CLIENT_COMPANY_DATA, response.data.data);
          return response.data;
        }
      })
      .catch((error) => {
        setCompanyLoading(false);
        setClientCompanyData({
          error: error?.response?.data?.message,
        });
      });
  };

  const handleUserSwitchPaths = (name, id, type, isPrimary, isSpouseAccount) => {
    let newPath = {
      title: name,
      code: id,
      type: type,
      isPrimary: isPrimary,
      isSpouseAccount: isSpouseAccount,
      isMainProfile: clientUserId === id || clientSpouseId === id ? true : false,
    };

    if (
      isEmpty(getLocalStorage(CLIENT_USER_PATHS)?.filter((path) => path?.code === newPath?.code))
    ) {
      setProfileSwitchPaths([...profileSwitchPaths, newPath]);
      setLocalStorage(CLIENT_USER_PATHS, [...profileSwitchPaths, newPath]);
    } else {
      let indexOfExistingItem = profileSwitchPaths
        ?.map((path) => path?.code)
        ?.indexOf(newPath?.code);
      setProfileSwitchPaths(
        profileSwitchPaths?.filter((path, index) => index <= indexOfExistingItem)
      );
      setLocalStorage(
        CLIENT_USER_PATHS,
        profileSwitchPaths?.filter((path, index) => index <= indexOfExistingItem)
      );
    }

    localStorage.setItem('change-user-event-client', 'change' + Math.random());
  };

  const handleAssociationChange = (type, id, name, isPrimary, isSpouseAccount) => {
    setLocalStorage(CLIENT_USER_FULL_NAME, name);
    /**
     * This condition is to check if the incomming id is of
     * the spouse account or not, if yes then set the primary client id as
     * the main id else set whatever is comming.
     */
    if (clientProfile?.spouseId === id) {
      setLocalStorage(CLIENT_SELECTED_ASSOCIATION_ID, clientProfile?.id);
      setSelectedAssociationId(clientProfile?.id);
    } else {
      setLocalStorage(CLIENT_SELECTED_ASSOCIATION_ID, id);
      setSelectedAssociationId(id);
    }

    setSwitchedClientId(id);
    setLocalStorage(CLIENT_SWITCHED_ID, id);
    setLocalStorage(CLIENT_SELECTED_ASSOCIATION_TYPE, type);
    setLocalStorage(CLIENT_IS_PRIMARY_ACCOUNT, isPrimary);
    setLocalStorage(CLIENT_IS_SPOUSE, isSpouseAccount);
    setSelectedAssociationType(type);
    setIsPrimaryAccount(isPrimary);
    setIsSpouseAccount(isSpouseAccount);
    handleUserSwitchPaths(name, id, type, isPrimary, isSpouseAccount);
    history.push(`/client/dashboard`);
  };

  const clientLogout = () => {
    clientLogoutLocalStorage();

    setIsPrimaryAccount(false);
    setSelectedAssociationId(undefined);
    setSelectedAssociationType(undefined);
    setClientType(undefined);
    setClientUserId(undefined);
    setClientSpouseId(undefined);
    setClientEmail(undefined);
    setUserClientFullName(undefined);
    setClientCountryCode(undefined);
    setTaxRepresentativeEmail(undefined);
    setTaxRepresentativeID(undefined);
    setTaxRepresentativeName(undefined);
    setEnforceUpdateStatus(false);
    setUser({});
    setClientAuthenticated(false);
    clientCompanyDataClean();
    setFields({ userId: '', password: '', accessCode: '' });
    setIsSpouseAccount(false);
    setClientEncryptedKey({});
    setProfileSwitchPaths([]);
    setVerificationId('');
    setClientSelectedLanguage('en');
    setSwitchedClientId(undefined);
    history.push(clientAccessCode ? `/${clientAccessCode}` : '/');
    clientAppUnregisterFirebase();
  };

  return (
    <ClientAuthContext.Provider
      {...props}
      value={{
        ...state,
        message: message,
        setMessage: setMessage,
        loading: loading,
        fields: fields,
        setLoading: setLoading,
        sideBarMenu: sideBarMenu,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        clientCompanyDataClean: clientCompanyDataClean,
        clientCompanyDataFetch: clientCompanyDataFetch,
        clientLogin: clientLogin,
        clientLogout: clientLogout,
        setClientType: setClientType,
        handleAssociationChange: handleAssociationChange,
        setEnforceUpdateStatus: setEnforceUpdateStatus,
        setFields: setFields,
        setPrimaryColor: setPrimaryColor,
        setSecondaryColor: setSecondaryColor,
        cleanClientAuthContextClean: cleanClientAuthContextClean,
        setDocumentUploaded: setDocumentUploaded,
        setUserClientFullName: setUserClientFullName,
        setProfileSwitchPaths: setProfileSwitchPaths,
        setClientCountryCode: setClientCountryCode,
        setVerificationId: setVerificationId,
        setClientSelectedLanguage: setClientSelectedLanguage,
        setTaxRepresentativeEmail: setTaxRepresentativeEmail,
        setTaxRepresentativeID: setTaxRepresentativeID,
        setTaxRepresentativeName: setTaxRepresentativeName,
        setSwitchedClientId: setSwitchedClientId,
        setClientFormError: setClientFormError,
        clientLoginSuccess: clientLoginSuccess,
      }}
    >
      {props.children}
    </ClientAuthContext.Provider>
  );
};

export { ClientAuthProvider, ClientAuthContext };
